/**
 * AI Service
 * OpenRouter AI API entegrasyonu için servis sınıfı
 */

import api from '../lib/api';

export interface ConversationSummaryResponse {
  success: boolean;
  message: string;
  data: {
    summary: string;
    totalMessages: number;
    originalConversationLength: number;
    meta: {
      model: string;
      requestedAt: string;
      userId: string;
      ticketId?: string;
    };
  };
}

export interface ResponseSuggestionResponse {
  success: boolean;
  message: string;
  data: {
    suggestion: string;
    conversationLength: number;
    meta: {
      model: string;
      requestedAt: string;
      userId: string;
    };
  };
}

export interface ConversationMessage {
  role: 'customer' | 'agent';
  content: string;
}

export interface AISessionMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface TicketInfo {
  subject?: string;
  status?: string;
  priority?: string;
  organizationName?: string;
  customerEmail?: string;
  customerName?: string;
  ticketNumber?: string;
}

export interface OrderInfo {
  orderNumber: string;
  date?: string;
  total?: string;
  status?: string;
  items?: { name: string; quantity: number; price?: string }[];
}

export interface AIChatRequest {
  userPrompt: string;
  sessionHistory?: AISessionMessage[];
  ticketConversation?: ConversationMessage[];
  ticketId?: string;
  ticketInfo?: TicketInfo;
  orders?: OrderInfo[];
  provider?: 'claude' | 'gpt'; // @claude / etiketsiz → claude, @chatgpt → gpt
}

export interface AIChatResponse {
  success: boolean;
  message: string;
  blocked?: boolean;
  reason?: string;
  data?: {
    response: string;
    meta: {
      model: string;
      generatedAt: string;
      userId: string;
      ticketId?: string;
    };
  };
}

export interface TranslationResponse {
  success: boolean;
  message: string;
  data: {
    translatedContent: string;
    targetLanguage: string;
    originalLength: number;
    translatedLength: number;
    meta: {
      model: string;
      translatedAt: string;
      userId: string;
    };
  };
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
}

// Supported languages for translation
export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'de', name: 'German', nativeName: 'Deutsch', countryCode: 'de' },
  { code: 'en', name: 'English', nativeName: 'English', countryCode: 'gb' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', countryCode: 'tr' },
  { code: 'fr', name: 'French', nativeName: 'Français', countryCode: 'fr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', countryCode: 'es' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', countryCode: 'it' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', countryCode: 'pt' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', countryCode: 'nl' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', countryCode: 'pl' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', countryCode: 'ru' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', countryCode: 'cn' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', countryCode: 'jp' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', countryCode: 'sa' },
];

class AIService {
  /**
   * Ticket'ın tüm mesajlarından conversation summary oluşturur
   * Eğer ticketId verilirse tüm ticket mesajları, yoksa sadece firstMessage kullanılır
   */
  async generateConversationSummary(firstMessage: string, ticketId?: string): Promise<ConversationSummaryResponse> {
    const response = await api.post<ConversationSummaryResponse>('/ai/conversation-summary', {
      firstMessage,
      ticketId
    });
    return response.data;
  }

  /**
   * Tüm conversation'dan AI response suggestion oluşturur
   */
  async generateResponseSuggestion(conversation: ConversationMessage[]): Promise<ResponseSuggestionResponse> {
    const response = await api.post<ResponseSuggestionResponse>('/ai/response-suggestion', {
      conversation
    });
    return response.data;
  }

  /**
   * İçeriği hedef dile çevirir
   */
  async translateContent(content: string, targetLanguage: string): Promise<TranslationResponse> {
    const response = await api.post<TranslationResponse>('/ai/translate', {
      content,
      targetLanguage
    });
    return response.data;
  }

  /**
   * AI Chat - Session-based conversation with memory
   * User types instructions, AI generates customer response
   */
  async chat(request: AIChatRequest): Promise<AIChatResponse> {
    const response = await api.post<AIChatResponse>('/ai/chat', request);
    return response.data;
  }

  /**
   * Get pre-generated AI suggestion for a ticket
   */
  async getSuggestion(ticketId: string): Promise<AISuggestionResponse> {
    const response = await api.get<AISuggestionResponse>(`/ai/suggestion/${ticketId}`);
    return response.data;
  }

  /**
   * Mark AI suggestion as used
   */
  async markSuggestionUsed(suggestionId: string): Promise<void> {
    await api.post(`/ai/suggestion/${suggestionId}/use`);
  }

  /**
   * Manually generate AI suggestion for a ticket
   */
  async generateSuggestion(ticketId: string): Promise<AISuggestionResponse> {
    const response = await api.post<AISuggestionResponse>('/ai/suggestion/generate', { ticketId });
    return response.data;
  }

  // ============================================================
  // AI Ticket Processor Methods
  // ============================================================

  /**
   * Get AI analysis draft for a ticket
   */
  // Agent Cockpit: gespeicherte, geprüfte Antwort
  async getAgentDraft(ticketId: string): Promise<{ success: boolean; data: { reply: string | null; at: string | null } }> {
    const r = await api.get(`/ai/tickets/${ticketId}/agent-draft`);
    return r.data;
  }
  async saveAgentDraft(ticketId: string, reply: string): Promise<{ success: boolean }> {
    const r = await api.put(`/ai/tickets/${ticketId}/agent-draft`, { reply });
    return r.data;
  }

  async getTicketAIDraft(ticketId: string): Promise<TicketAIDraftResponse> {
    const response = await api.get<TicketAIDraftResponse>(`/ai/tickets/${ticketId}/draft`);
    return response.data;
  }

  /**
   * Manually trigger AI analysis for a ticket
   */
  async processTicketAI(ticketId: string): Promise<TicketAIDraftResponse> {
    const response = await api.post<TicketAIDraftResponse>(`/ai/tickets/${ticketId}/process`);
    return response.data;
  }

  /**
   * Regenerate AI draft with optional additional instructions
   */
  async regenerateAIDraft(ticketId: string, instructions?: string): Promise<TicketAIDraftResponse> {
    const response = await api.post<TicketAIDraftResponse>(`/ai/tickets/${ticketId}/regenerate`, {
      additionalInstructions: instructions
    });
    return response.data;
  }

  /**
   * Send AI response to customer
   */
  async sendAIResponse(ticketId: string, response: string, edited: boolean): Promise<void> {
    await api.post(`/ai/tickets/${ticketId}/send`, { response, edited });
  }

  /**
   * Approve an action in the AI action list
   */
  async approveAction(ticketId: string, actionIndex: number): Promise<void> {
    await api.post(`/ai/tickets/${ticketId}/approve-action`, { actionIndex });
  }
}

// AI Ticket Processor Interfaces
export interface AIAction {
  type: string;
  description: string;
  details?: string;
  status: 'pending' | 'approved';
}

export interface AISimilarTicket {
  ticket_id: string;
  ticket_number: string;
  subject: string;
  similarity: string;
}

export interface TicketAIDraft {
  ai_flag: 'green' | 'yellow' | 'red' | null;
  ai_category: string | null;
  ai_confidence: number | null;
  ai_sentiment_score: number | null;
  ai_draft_response: string | null;
  ai_actions: AIAction[] | null;
  ai_similar_tickets: AISimilarTicket[] | null;
  ai_research_notes: string | null;
  ai_detected_language: string | null;
  ai_processed_at: string | null;
  ai_available?: boolean;
  ai_reason?: string;
  ai_block_message?: string | null;
}

export interface TicketAIDraftResponse {
  success: boolean;
  data: TicketAIDraft;
}

export interface AISuggestion {
  id: string;
  ticket_id: string;
  organization_id: string;
  suggestion_text: string;
  conversation_length: number;
  trigger_type: 'new_ticket' | 'reply' | 'manual';
  generated_at: string;
  is_used: boolean;
  used_at?: string;
  used_by?: string;
  is_stale: boolean;
}

export interface AISuggestionResponse {
  success: boolean;
  data: AISuggestion | null;
  message?: string;
}

export const aiService = new AIService();
export default aiService;